import React, { useState, useRef, useCallback } from 'react';

const PhotoPositioner = ({ 
  imageUrl, 
  onPositionChange, 
  initialX = 50, 
  initialY = 50 
}) => {
  // Fixed dimensions matching the Band Members page
  const containerWidth = 350;
  const containerHeight = 250;
  
  const [position, setPosition] = useState({ x: initialX, y: initialY });
  const [isDragging, setIsDragging] = useState(false);
  const [dragStart, setDragStart] = useState({ x: 0, y: 0 });
  const containerRef = useRef(null);

  const handleMouseDown = useCallback((e) => {
    if (!containerRef.current) return;
    
    const rect = containerRef.current.getBoundingClientRect();
    const startX = e.clientX - rect.left;
    const startY = e.clientY - rect.top;
    
    setIsDragging(true);
    setDragStart({ x: startX, y: startY });
    e.preventDefault();
  }, []);

  const handleMouseMove = useCallback((e) => {
    if (!isDragging || !containerRef.current) return;
    
    const rect = containerRef.current.getBoundingClientRect();
    const currentX = e.clientX - rect.left;
    const currentY = e.clientY - rect.top;
    
    const deltaX = currentX - dragStart.x;
    const deltaY = currentY - dragStart.y;
    
    // Convert pixel movement to percentage with reduced sensitivity for better control
    const percentageX = (deltaX / containerWidth) * 30;
    const percentageY = (deltaY / containerHeight) * 30;
    
    const newX = Math.max(0, Math.min(100, position.x - percentageX));
    const newY = Math.max(0, Math.min(100, position.y - percentageY));
    
    setPosition({ x: newX, y: newY });
    setDragStart({ x: currentX, y: currentY });
    
    // Notify parent component
    onPositionChange && onPositionChange({ x: newX, y: newY });
  }, [isDragging, dragStart, position, onPositionChange]);

  const handleMouseUp = useCallback(() => {
    setIsDragging(false);
  }, []);

  // Add global mouse events when dragging
  React.useEffect(() => {
    if (isDragging) {
      document.addEventListener('mousemove', handleMouseMove);
      document.addEventListener('mouseup', handleMouseUp);
      return () => {
        document.removeEventListener('mousemove', handleMouseMove);
        document.removeEventListener('mouseup', handleMouseUp);
      };
    }
  }, [isDragging, handleMouseMove, handleMouseUp]);

  if (!imageUrl) {
    return (
      <div style={{ margin: '1rem 0' }}>
        <div style={{
          color: 'var(--retro-cream)',
          fontWeight: 'bold',
          marginBottom: '0.5rem',
          fontSize: '0.875rem'
        }}>
          Website Preview:
        </div>
        <div style={{
          width: `${containerWidth}px`,
          height: `${containerHeight}px`,
          border: '2px dashed var(--retro-red)',
          borderRadius: '8px',
          display: 'flex',
          alignItems: 'center',
          justifyContent: 'center',
          color: 'var(--retro-red)',
          fontSize: '14px',
          backgroundColor: 'var(--retro-black)'
        }}>
          Select an image to see preview
        </div>
      </div>
    );
  }

  return (
    <div style={{ margin: '1rem 0' }}>
      <div style={{
        color: 'var(--retro-cream)',
        fontWeight: 'bold',
        marginBottom: '0.5rem',
        fontSize: '0.875rem'
      }}>
        Website Preview (drag image to reposition):
      </div>
      
      <div 
        ref={containerRef}
        style={{
          width: `${containerWidth}px`,
          height: `${containerHeight}px`,
          border: '3px solid var(--retro-red)',
          borderRadius: '8px',
          overflow: 'hidden',
          position: 'relative',
          cursor: isDragging ? 'grabbing' : 'grab',
          backgroundColor: 'var(--retro-black)',
          display: 'block',
          flexShrink: 0
        }}
        onMouseDown={handleMouseDown}
      >
        <img
          src={imageUrl}
          alt="Position preview"
          style={{
            position: 'absolute',
            top: 0,
            left: 0,
            width: '100%',
            height: '100%',
            objectFit: 'cover',
            objectPosition: `${position.x}% ${position.y}%`,
            userSelect: 'none',
            pointerEvents: 'none'
          }}
          draggable={false}
        />
        
        {/* Center guide dot */}
        <div style={{
          position: 'absolute',
          top: '50%',
          left: '50%',
          transform: 'translate(-50%, -50%)',
          width: '16px',
          height: '16px',
          border: '2px solid rgba(255, 255, 255, 0.9)',
          borderRadius: '50%',
          backgroundColor: 'rgba(229, 0, 0, 0.4)',
          pointerEvents: 'none',
          zIndex: 10,
          opacity: isDragging ? 1 : 0.7
        }} />
        
        {/* Size indicator */}
        <div style={{
          position: 'absolute',
          top: '4px',
          left: '4px',
          color: 'rgba(255, 255, 255, 0.8)',
          fontSize: '10px',
          backgroundColor: 'rgba(0, 0, 0, 0.5)',
          padding: '2px 4px',
          borderRadius: '3px',
          pointerEvents: 'none',
          zIndex: 10
        }}>
          350×250px
        </div>
      </div>
      
      <div style={{
        marginTop: '0.5rem',
        fontSize: '0.75rem',
        color: 'var(--retro-cream)',
        opacity: 0.8,
        textAlign: 'center',
        maxWidth: `${containerWidth}px`
      }}>
        This shows exactly how it will appear on the Band Members page. 
        Position: {Math.round(position.x)}%, {Math.round(position.y)}%
      </div>
    </div>
  );
};

export default PhotoPositioner;